import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { fadeIn, staggerContainer } from './utils/animations';

const Reviews = () => {
  const reviewsRef = useRef(null);
  const [hoverState, setHoverState] = useState(false);
  // Add animation state refs to persist across renders
  const scrollPositionRef = useRef(0);
  const animationIdRef = useRef(null);
  
  // Updated review data for influencer and reseller-focused web development
  const reviews = [
    {
      id: 1, 
      name: "Sarah Johnson",
      role: "Fashion Influencer",
      company: "@style_by_sarah",
      rating: 5,
      text: "Wesley built me a site that matches my Instagram aesthetic perfectly. My merch sales tripled in the first month!",
      avatar: "SJ"
    },
    {
      id: 2,
      name: "Michael Chen",
      role: "Sneaker Reseller",
      company: "KickFlip Market",
      rating: 5,
      text: "This guy gets it. My sneaker shop actually looks legit now and the checkout process is smooth. Worth every penny.",
      avatar: "MC"
    },
    {
      id: 3,
      name: "Ava Williams",
      role: "Fitness Coach",
      company: "Fit With Ava",
      rating: 4,
      text: "My subscribers love the new membership portal. Had a few minor issues but Wesley fixed them right away.",
      avatar: "AW"
    },
    {
      id: 4,
      name: "Daniel Rodriguez",
      role: "Vintage Collector",
      company: "RetroFinds",
      rating: 5,
      text: "Finally found someone who could showcase my vintage collection properly. The site is lightning fast too!",
      avatar: "DR"
    },
    {
      id: 5,
      name: "Emma Taylor",
      role: "Beauty Blogger",
      company: "Glow By Emma",
      rating: 5,
      text: "My affiliate revenue is up 70% since launching Wesley's site. He really understands what content creators need.",
      avatar: "ET"
    }
  ];

  // Create duplicated data array for infinite scrolling
  const duplicatedReviews = [...reviews, ...reviews];

  // Improved animation effect with smooth transitions between speeds
  useEffect(() => {
    const container = reviewsRef.current;
    if (!container) return;

    const normalSpeed = 0.5; // normal pixels per frame
    const slowSpeed = 0.1;   // slow pixels per frame when hovering
    
    const animate = () => {
      if (!container) return;
      
      // Use slower speed on hover instead of completely stopping
      const currentSpeed = hoverState ? slowSpeed : normalSpeed;
      
      // Update the ref value directly to maintain scroll position
      scrollPositionRef.current += currentSpeed;
      
      // Calculate width of a single set of reviews
      const reviewWidth = container.scrollWidth / 2;
      
      // When we reach the original set's width, reset to beginning without visual jump
      if (scrollPositionRef.current >= reviewWidth) {
        scrollPositionRef.current = 0;
        container.scrollLeft = 0;
      } else {
        container.scrollLeft = scrollPositionRef.current;
      }
      
      // Store animation ID in ref for persistent access
      animationIdRef.current = requestAnimationFrame(animate);
    };
    
    // Clear any existing animation before starting new one
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }
    
    // Start the animation
    animationIdRef.current = requestAnimationFrame(animate);
    
    // Cleanup function
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [hoverState]); // Only re-run when hover state changes

  return (
    <motion.div 
      variants={staggerContainer(0.1, 0.2)}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.25 }}
      className="container mx-auto px-4 py-16"
    >
      <motion.div 
        variants={fadeIn('up', 0.2)}
        className="relative mb-16 text-center"
      >
        <div className="absolute left-1/2 top-1/2 -z-10 h-36 w-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl" />
        <h2 className="relative text-4xl font-bold text-white md:text-5xl lg:text-6xl">
          What My <span className="bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">Clients</span> Say
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-400">
          Don't just take my word for it. See what brands have achieved with my web development solutions.
        </p>
      </motion.div>

      <motion.div 
        variants={fadeIn('up', 0.5)}
        className="relative overflow-hidden pb-8"
      >
        {/* Remove the left and right fade overlays and use a mask instead */}
        
        <div 
          ref={reviewsRef}
          className="flex overflow-x-scroll pt-4 pb-12 gap-6 no-scrollbar min-h-[300px]"
          style={{ 
            scrollBehavior: 'auto',
            maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
          }}
        >
          {/* Add extra padding at start for better shadow effect */}
          <div className="pl-4 md:pl-12"></div>
          
          {/* Render both original and duplicated reviews */}
          {duplicatedReviews.map((review, index) => (
            <motion.div 
              key={`${review.id}-${index}`} 
              className="review-card flex-shrink-0 w-[350px]"
              whileHover={{ 
                y: -10,
                transition: { type: "spring", stiffness: 300 }
              }}
              onMouseEnter={() => setHoverState(true)}
              onMouseLeave={() => setHoverState(false)}
              initial={false}
            >
              <div className="group relative">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-[1px] shadow-2xl transition-all duration-300 hover:shadow-cyan-500/25">
                  <div className="absolute inset-0 bg-gradient-to-b from-cyan-500 to-blue-500 opacity-20" />
                  <div className="relative rounded-2xl bg-gradient-to-b from-slate-950 to-slate-900 p-6">
                    <div className="absolute -left-16 -top-16 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                    <div className="absolute -bottom-8 -right-8 h-32 w-32 rounded-full bg-gradient-to-br from-blue-500/20 to-cyan-500/0 blur-2xl transition-all duration-500 group-hover:scale-150 group-hover:opacity-70" />
                    
                    <div className="relative flex mb-4 items-center">
                      <motion.div 
                        className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-lg"
                        whileHover={{ scale: 1.1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      >
                        {review.avatar}
                      </motion.div>
                      <div className="ml-4">
                        <h3 className="text-lg font-semibold text-white">{review.name}</h3>
                        <p className="text-sm text-slate-400">{review.role}, {review.company}</p>
                      </div>
                    </div>
                    
                    <div className="flex mb-3">
                      {[...Array(5)].map((_, i) => (
                        <motion.svg 
                          key={i} 
                          className={`h-5 w-5 ${i < review.rating ? 'text-cyan-500' : 'text-slate-600'}`} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 * i }}
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </motion.svg>
                      ))}
                    </div>
                    
                    <motion.p 
                      className="text-sm text-slate-300 mb-2"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      "{review.text}"
                    </motion.p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          
          {/* Add extra padding at end */}
          <div className="pr-4 md:pr-12"></div>
        </div>
      </motion.div>
      
      {/* Connecting gradient element that maintains the cyan/blue theme */}
      <div className="relative h-32 -mt-8 mb-0">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-transparent"></div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-48 w-full rounded-full bg-gradient-to-b from-cyan-500/10 via-blue-500/5 to-transparent blur-3xl"></div>
      </div>
    </motion.div>
  );
};

export default Reviews;
